import React, { useEffect } from 'react';
import { Routes, Route, useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import Index from "@/pages/Index";
import TransferPatientsPage from "@/pages/TransferPatientsPage";
import TransferPatientsListPage from "@/pages/TransferPatientsListPage";
import NotFound from "@/pages/NotFound";
import MainDashboard from "@/components/common/MainDashboard";
import CSPatientList from "@/components/features/patient/CSPatientList";
import PEPatientList from "@/components/features/patient/PEPatientList";
import GlobalSearch from "@/components/features/search/GlobalSearch";
import ManageClinicAppointmentSlotsRefactored from "@/components/features/appointment-slots/ManageClinicAppointmentSlotsRefactored";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import ManageGroupWrapper from "@/pages/ea/ManageGroupWrapper";
import ArchivedGroupsWrapper from "@/pages/ea/ArchivedGroupsWrapper";
import ManageVPEPageWrapper from "@/pages/ea/ManageVPEPageWrapper";
import SMSPage from "@/pages/SMSPage";
import CallHistoryPage from "@/pages/CallHistoryPage";
import ESDashboard from "@/pages/es/ESDashboard";
import ScheduledAppointmentsPage from "@/pages/ScheduledAppointmentsPage";
import AddPatientPage from "@/pages/AddPatientPage";
import FaxPage from "@/pages/FaxPage";
import { AdminLayout } from './v2/layouts/AdminLayout';
import Dashboard from './v2/pages/admin/dashboard/Dashboard';
import Users from './v2/pages/admin/users/Users';
import AddUser from './v2/pages/admin/users/AddUser';
import EditUser from './v2/pages/admin/users/EditUser';
import AddUserGroup from './v2/pages/admin/user-group/AddUserGroup';
import EditUserGroup from './v2/pages/admin/user-group/EditUserGroup';
import ProgramType from './v2/pages/admin/program-type/ProgramType';
import AddProgramType from './v2/pages/admin/program-type/AddProgramType';
import EditProgramType from './v2/pages/admin/program-type/EditProgramType';
import AddPatientProgramStatus from './v2/pages/admin/program-status/AddPatientProgramStatus';
import EditPatientProgramStatus from './v2/pages/admin/program-status/EditPatientProgramStatus';
import PatientGroups from './v2/pages/admin/patient-group/PatientGroups';
import CreateGroupByFilters from './v2/pages/admin/patient-group/CreateGroupByFilters';
import CreateGroupByEMRs from './v2/pages/admin/patient-group/CreateGroupByEMRs';
import CreateGroupByExcel from './v2/pages/admin/patient-group/CreateGroupByExcel';
import ArchivedPatientGroups from './v2/pages/admin/patient-group/ArchivedPatientGroups';
import EditPatientGroup from './v2/pages/admin/patient-group/EditPatientGroup';
import DuplicatePatientGroup from './v2/pages/admin/patient-group/DuplicatePatientGroup';
import RemovePatients from './v2/pages/admin/patient-group/RemovePatients';
import AddPatients from './v2/pages/admin/patient-group/AddPatients';
import DuplicateRemovePatients from './v2/pages/admin/patient-group/DuplicateRemovePatients';
import DuplicateAddPatients from './v2/pages/admin/patient-group/DuplicateAddPatients';
import Workflow from './v2/pages/admin/workflow/Workflow';
import AddWorkflow from './v2/pages/admin/workflow/AddWorkflow';
import EditWorkflow from './v2/pages/admin/workflow/EditWorkflow';
import AddWorkflowStatus from './v2/pages/admin/workflow-status/AddWorkflowStatus';
import EditWorkflowStatus from './v2/pages/admin/workflow-status/EditWorkflowStatus';
import AddTask from './v2/pages/admin/task/AddTask';
import EditTask from './v2/pages/admin/task/EditTask';
import Activities from './v2/pages/admin/activity/Activities';
import AddActivity from './v2/pages/admin/activity/AddActivity';
import EditActivity from './v2/pages/admin/activity/EditActivity';
import DuplicateActivity from './v2/pages/admin/activity/DuplicateActivity';
import FacilityAvailability from './v2/pages/admin/facility-availability/FacilityAvailability';
import AddFacilityAvailability from './v2/pages/admin/facility-availability/AddFacilityAvailability';
import EditFacilityAvailability from './v2/pages/admin/facility-availability/EditFacilityAvailability';
import ClinicUserGroups from './v2/pages/admin/clinic-user-groups/ClinicUserGroups';
import AddClinicUserGroup from './v2/pages/admin/clinic-user-groups/AddClinicUserGroup';
import EditClinicUserGroup from './v2/pages/admin/clinic-user-groups/EditClinicUserGroup';
import DashboardItemsPage from '@/pages/DashboardItemsPage';
import MicrosoftCallbackPage from '@/pages/MicrosoftCallbackPage';
import DashboardConfigPage from './v2/pages/admin/dashboard-config/DashboardConfigPage';
import DashboardConfigViewPage from './v2/pages/admin/dashboard-config/DashboardConfigViewPage';
import UserDashboardPage from './v2/pages/user-dashboard/UserDashboardPage';

// Wrapper component for GlobalSearch to handle back navigation to home
const GlobalSearchWrapper = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Set different titles based on route
    if (location.pathname === '/admin/patients') {
      document.title = "Patients | Admin";
    } else {
      document.title = "Search Patients | Admin";
    }
  }, [location.pathname]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <GlobalSearch onBack={handleBack} />
  );
};

// Wrapper component for Appointment Slots to handle back navigation
const AppointmentSlotsWrapper = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <ManageClinicAppointmentSlotsRefactored onBack={handleBack} />
  );
};

const AppRoutes: React.FC = () => {
  return (
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth/microsoft/callback" element={<MicrosoftCallbackPage />} />

      {/* Common Dashboard Route - All user types use this with AdminLayout */}
      <Route path="/dashboard" element={
        <ProtectedRoute requiredUserType={['EA', 'CA', 'CS', 'PA', 'PE', 'ES']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<MainDashboard />} />
      </Route>

      <Route path="/global-search" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<GlobalSearchWrapper />} />
      </Route>
      <Route path="/appointment-slots" element={
        <ProtectedRoute requiredUserType={['PA']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AppointmentSlotsWrapper />} />
      </Route>
      <Route path="/sms" element={
        <ProtectedRoute requiredUserType={['PA', 'CA', 'EA', 'ES', 'VPE']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SMSPage />} />
      </Route>
      <Route path="/fax" element={
        <ProtectedRoute requiredUserType={['PA', 'EA', 'ES', 'VPE', 'ED']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<FaxPage />} />
      </Route>
      <Route path="/call-history" element={
        <ProtectedRoute requiredUserType={['EA', 'ES']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CallHistoryPage />} />
      </Route>
      <Route path="/scheduled-appointments" element={
        <ProtectedRoute requiredUserType={['EA', 'ED', 'ES', 'CS', 'PE']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ScheduledAppointmentsPage />} />
      </Route>
      <Route path="/es-dashboard" element={
        <ProtectedRoute requiredUserType={['ES', 'CS', 'PE', 'PA', 'EA', 'ED']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ESDashboard />} />
      </Route>
      <Route path="/add-patient" element={
        <ProtectedRoute requiredUserType={['ES', 'CS', 'PE', 'PA', 'EA', 'ED', 'CA']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AddPatientPage />} />
      </Route>
      <Route path="/transfer-patients" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<TransferPatientsPage />} />
      </Route>
      <Route path="/transfer-patients-list" element={
        <ProtectedRoute requiredUserType={['ED']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<TransferPatientsListPage />} />
      </Route>
      <Route path="/pe-followup-patients" element={
        <ProtectedRoute requiredUserType={['PE']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PEPatientList type="followup" />} />
      </Route>
      <Route path="/pe-deferred-patients" element={
        <ProtectedRoute requiredUserType={['PE']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<PEPatientList type="deferred" />} />
      </Route>
      <Route path="/cs-followup-patients" element={
        <ProtectedRoute requiredUserType={['CS']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CSPatientList />} />
      </Route>
      <Route path="/cs-deferred-patients" element={
        <ProtectedRoute requiredUserType={['CS']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<CSPatientList />} />
      </Route>

      {/* Group Operations Route - Unified route for create/duplicate/edit group operations */}
      <Route path="/group-operations/:operation/:id?" element={
        <ProtectedRoute requiredUserType={['EA']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ManageGroupWrapper />} />
      </Route>

      {/* Archived Groups Route */}
      <Route path="/archived-groups" element={
        <ProtectedRoute requiredUserType={['EA']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ArchivedGroupsWrapper />} />
      </Route>

      {/* Manage ES Route */}
      <Route path="/manage-es" element={
        <ProtectedRoute requiredUserType={['EA']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ManageVPEPageWrapper />} />
      </Route>

      {/* Enroller admin
      <Route path="/admin" element={
        <ProtectedRoute requiredUserType={['ED']}>
          <ManageVPEPageWrapper />
        </ProtectedRoute>
      } /> */}
      <Route path="/admin" element={
        <ProtectedRoute requiredUserType={['ED']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
          <Route index element={<Navigate to="/admin/dashboard-config" replace />} />
          <Route path="dashboard" element={ <Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="users/add" element={<AddUser />} />
          <Route path="users/edit/:id" element={<EditUser />} />
          <Route path="users/groups/add" element={<AddUserGroup />} />
          <Route path="users/groups/edit/:id" element={<EditUserGroup />} />
          <Route path="program-type" element={<ProgramType />} />
          <Route path="program-type/add" element={<AddProgramType />} />
          <Route path="program-type/edit/:id" element={<EditProgramType />} />
          <Route path="patient-program-status/add" element={<AddPatientProgramStatus />} />
          <Route path="patient-program-status/edit/:id" element={<EditPatientProgramStatus />} />
          <Route path="patient-groups" element={<PatientGroups />} />
          <Route path="patient-groups/archived" element={<ArchivedPatientGroups />} />
          <Route path="patient-groups/create-by-filters" element={<CreateGroupByFilters />} />
          <Route path="patient-groups/create-by-emrs" element={<CreateGroupByEMRs />} />
          <Route path="patient-groups/create-by-excel" element={<CreateGroupByExcel />} />
          <Route path="patient-groups/edit/:patientGroupId" element={<EditPatientGroup />} />
          <Route path="patient-groups/duplicate/:patientGroupId" element={<DuplicatePatientGroup />} />
          <Route path="patient-groups/edit/:patientGroupId/remove-patients" element={<RemovePatients />} />
          <Route path="patient-groups/edit/:patientGroupId/add-patients" element={<AddPatients />} />
          <Route path="patient-groups/duplicate/:patientGroupId/remove-patients" element={<DuplicateRemovePatients />} />
          <Route path="patient-groups/duplicate/:patientGroupId/add-patients" element={<DuplicateAddPatients />} />
          <Route path="workflow" element={<Workflow />} />
          <Route path="workflow/add" element={<AddWorkflow />} />
          <Route path="workflow/edit/:id" element={<EditWorkflow />} />
          <Route path="workflow-status/add" element={<AddWorkflowStatus />} />
          <Route path="workflow-status/edit/:id" element={<EditWorkflowStatus />} />
          <Route path="tasks/add" element={<AddTask />} />
          <Route path="tasks/edit/:id" element={<EditTask />} />
          <Route path="activity" element={<Activities />} />
          <Route path="activity/add" element={<AddActivity />} />
          <Route path="activity/edit/:id" element={<EditActivity />} />
          <Route path="activity/duplicate/:id" element={<DuplicateActivity />} />
          <Route path="facility-availability" element={<FacilityAvailability />} />
          <Route path="facility-availability/add" element={<AddFacilityAvailability />} />
          <Route path="facility-availability/edit/:id" element={<EditFacilityAvailability />} />
          <Route path="clinic-user-groups" element={<ClinicUserGroups />} />
          <Route path="clinic-user-groups/add" element={<AddClinicUserGroup />} />
          <Route path="clinic-user-groups/edit/:id" element={<EditClinicUserGroup />} />
          <Route path="dashboard-config" element={<DashboardConfigPage />} />
          <Route path="dashboard-config/:id" element={<DashboardConfigViewPage />} />
          <Route path="patients" element={<GlobalSearchWrapper />} />
          <Route path="sms" element={<SMSPage />} />
          <Route path="call-history" element={<CallHistoryPage />} />
      </Route>
      {/* Analytics Dashboard - standalone page accessible to ED users */}
      <Route path="/analytics" element={
        <ProtectedRoute requiredUserType={['ED']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardItemsPage />} />
      </Route>

      {/* User Dashboard - non-ED roles see their allocated Metabase dashboards */}
      <Route path="/user-dashboard" element={
        <ProtectedRoute requiredUserType={['EA', 'ES', 'CA', 'CS', 'PA', 'PE']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<UserDashboardPage />} />
      </Route>

      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;